import { useMessageReactions, GroupedReaction } from "@/hooks/useMessageReactions";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface MessageReactionsProps {
  messageId: string;
  messageType: 'channel' | 'direct';
  currentUserId: string;
}

export const MessageReactions = ({ messageId, messageType, currentUserId }: MessageReactionsProps) => {
  const { groupedReactions, toggleReaction, isLoading } = useMessageReactions(messageId, messageType);

  const reactions = groupedReactions(currentUserId);

  if (isLoading || reactions.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {reactions.map((reaction) => (
        <ReactionPill
          key={reaction.emoji}
          reaction={reaction}
          onClick={() => toggleReaction(reaction.emoji, currentUserId)}
        />
      ))}
    </div>
  );
};

interface ReactionPillProps {
  reaction: GroupedReaction;
  onClick: () => void;
}

const ReactionPill = ({ reaction, onClick }: ReactionPillProps) => {
  const userNames = reaction.users.map(u => u.name).join(', ');

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          className={cn(
            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors",
            "hover:bg-accent",
            reaction.hasReacted
              ? "bg-primary/10 border border-primary/30 text-primary"
              : "bg-muted border border-border"
          )}
        >
          <span>{reaction.emoji}</span>
          <span className="font-medium">{reaction.count}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <p className="text-xs">{userNames}</p>
      </TooltipContent>
    </Tooltip>
  );
};
